import { Radio, RadioChangeEvent } from 'antd';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import Button from 'components/Button/Button';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { TFileId, IDescriptionOfAspectRatio } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { createAppError } from 'utils/helpers';
import {
	FormImageUploaderContext,
	IFormImageUploaderContext,
	ILoadFromFilePathOptions,
} from '../formImageUploaderState/formImageUploaderContext';
import Text from 'components/Text/Text';
import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useState } from 'react';
import { getActualFilePath } from 'backend/additionalInformation';
import style from './Uploader.module.scss';

interface IProps {
	fileId: TFileId;
	deletePhotoHandler: () => void;
	containerClass?: string;
	noImageIconId?: string;
	loadFromFilePathOptions?: ILoadFromFilePathOptions;
	withValidation?: boolean;
	descriptionOfAspectRatio?: IDescriptionOfAspectRatio;
	infoContent?: React.ReactNode;
}

export const Uploader: React.FC<IProps> = ({
	fileId,
	containerClass,
	noImageIconId = 'menu',
	loadFromFilePathOptions,
	deletePhotoHandler,
	withValidation = false,
	descriptionOfAspectRatio,
	infoContent,
}) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const {
		setLoading,
		changeTypeOfModalWindow,
		setCroppedSizeSrc,
		setFullSizeSrc,
		setImageOrientation,
		setLoadingImageError,
		imageOrientation,
		isLoading,
		croppedSizeSrc,
		loadingImageError,
	} = useContext<IFormImageUploaderContext>(FormImageUploaderContext);
	const { t } = useTranslation('form');
	const [actualPreSignedUrl, setActualPreSignedUrl] = useState<string | null>(null);
	const isValidationSatisfied = !withValidation || !!(fileId || loadFromFilePathOptions?.filePath);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		isValidationSatisfied,
		'uploadIconNecessary',
		'addingIcon',
	);

	const containerClasses = classNames(style.container, containerClass);
	const deleteIconClasses = classNames(style.icon, style.deleteIcon);
	const deleteButtonClasses = classNames(style.deleteButton, {
		[style.buttonVisible]: croppedSizeSrc || loadFromFilePathOptions?.filePath,
	});
	const noImageContainerClasses = classNames(style.noImageContainer, {
		[style.error]: withValidation && isShowValidationError,
	});
	const buttonsWrapperClasses = classNames(style.buttonsWrapper, {
		[style.error]: withValidation && isShowValidationError,
	});

	const isImageLoaded = croppedSizeSrc || loadFromFilePathOptions?.filePath;

	useEffect(() => {
		const getPreSignedUrl = async () => {
			try {
				setLoading(true);
				const response = await getActualFilePath(loadFromFilePathOptions?.filePath || '');
				setActualPreSignedUrl(response.data.preSignedUrl);
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				const appError = createAppError(error);
				addAppError(appError);
			} finally {
				setLoading(false);
			}
		};

		if (loadFromFilePathOptions?.preSignedUrl) {
			setActualPreSignedUrl(loadFromFilePathOptions.preSignedUrl);
		} else if (!loadFromFilePathOptions?.preSignedUrl && loadFromFilePathOptions?.filePath) {
			getPreSignedUrl();
		}
	}, [loadFromFilePathOptions?.preSignedUrl, loadFromFilePathOptions?.filePath]);

	const deletePhoto = () => {
		setFullSizeSrc(null);
		setCroppedSizeSrc(null);
		setLoadingImageError(false);
		deletePhotoHandler();
	};

	// Clear img src after form reset
	useEffect(() => {
		if (!fileId && !loadFromFilePathOptions) {
			deletePhoto();
		}
	}, [fileId]);

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	const showErrorImage = () => {
		setLoadingImageError(true);
	};

	const renderImage = () => {
		if (isLoading) {
			return (
				<div className={style.imageContainer}>
					<LoaderWithProps loaderSize="big" />;
				</div>
			);
		}

		if (loadingImageError) {
			return (
				<div className={style.brokenImageContainer}>
					<svg className={style.brokenImageIcon}>
						<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
					</svg>
					<div>Error image</div>
				</div>
			);
		}

		if (croppedSizeSrc) {
			return (
				<div className={style.imageContainer}>
					<img className={style.image} src={croppedSizeSrc} alt="Photo" />
				</div>
			);
		}

		if (!croppedSizeSrc && !loadFromFilePathOptions?.filePath) {
			return (
				<>
					<div className={noImageContainerClasses}>
						<svg className={style.noImageIcon}>
							<use xlinkHref={`${breadcrumbs}#${noImageIconId}`} />
						</svg>
					</div>
					{withValidation && isShowValidationError && (
						<div className={style.imgErrorText}>{validationErrorMessage}</div>
					)}
				</>
			);
		}

		if (actualPreSignedUrl && !croppedSizeSrc) {
			return (
				<div className={style.imageContainer}>
					<img className={style.image} src={actualPreSignedUrl} alt="Photo" onError={showErrorImage} />
				</div>
			);
		}
	};

	const handleChange = (e: RadioChangeEvent) => {
		setImageOrientation(e.target.value);
	};

	return (
		<div className={containerClasses} data-test="FormImageUploader">
			<Radio.Group className={style.orientationSwitch} value={imageOrientation} onChange={handleChange}>
				<Radio value="horizontal">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="horizontalOrientationLabel"
						size="s"
					/>
				</Radio>
				<Radio value="vertical">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="verticalOrientationLabel"
						size="s"
					/>
				</Radio>
			</Radio.Group>
			<div className={style.renderImageWrapper}>
				{renderImage()}
				{infoContent && <div className={style.absoluteContent}>{infoContent}</div>}
			</div>
			{descriptionOfAspectRatio && (
				<div className={style.descriptionOfAspectRatio}>
					<div>
						{t('ratio')} <span>{descriptionOfAspectRatio[imageOrientation].ratio}</span>
					</div>
					<div>
						{t('optimalResolutions')}{' '}
						<span>
							{t('optimalResolutionValues', {
								a: descriptionOfAspectRatio[imageOrientation].optimalResolutions.a,
								b: descriptionOfAspectRatio[imageOrientation].optimalResolutions.b,
							})}
						</span>
					</div>
				</div>
			)}
			<div className={buttonsWrapperClasses}>
				<Button
					className={style.button}
					icon={
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#${!isImageLoaded ? 'uploadPhoto' : 'reload'}`} />
						</svg>
					}
					onClick={openCropper}>
					<Text
						translationNameSpace="form"
						translationKey={!isImageLoaded ? 'uploadPhotoButton' : 'replacePhotoButton'}
						asFragment={true}
					/>
				</Button>
				<Button
					className={deleteButtonClasses}
					theme="secondary"
					icon={
						<svg className={deleteIconClasses}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					}
					onClick={deletePhoto}>
					<Text translationNameSpace="form" translationKey="deletePhotoButton" asFragment={true} />
				</Button>
			</div>
		</div>
	);
};
